import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { host } from '~/config/settings/worktango.settings';
import { defaultLang } from '~/config/settings/app.settings';
import { TimeframeInput } from '~/gql/graphql';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import { RootState } from '../store';
import { BenchmarkResults, QuestionResults } from '~/interfaces/benchmark.interface';
import { fetchAttributeReportQuery, fetchBaseReportQuery } from '../react-query/reporting';
import { httpRequest } from '~/adapters/httpRequest';

const resetReportingState = createAction('survey-reporting/resetReportingState', ({ surveyIds }) => {
  return {
    payload: { surveyIds },
  };
});

const fetchReportingOverview = createAsyncThunk<
  {
    factors: any[];
    questions: any[];
    highlights: any;
    permissionLabel: any;
    entities: any;
  },
  {
    companyId: number;
    surveyIds: number[];
    selectedAttributes: { id: number; values: string[] }[];
    managerReportIds: number[];
    isHierarchyReport: boolean;
    lang?: string;
  }
>(
  'survey-reporting/fetchReportingOverview',
  async (
    { companyId, surveyIds, selectedAttributes, managerReportIds, isHierarchyReport, lang = defaultLang },
    { rejectWithValue },
  ) => {
    const filteredAttributes = selectedAttributes?.filter((attr) => attr?.values?.length > 0);
    const attributeIds = filteredAttributes?.map((attr) => attr.id);
    const attributeValues = filteredAttributes?.reduce(
      (acc, attr) => ({
        ...acc,
        [attr.id]: attr.values,
      }),
      {},
    );

    // REFACTOR: backend expects `attributeIds` to be in comma-format like
    // `attributeIds=1,2,3` whereas all the other values are expected to be in
    // index-format like `surveyIds[0]=1&surveyIds[1]=2&surveyIds[2]=3`.
    // So, attributeIds are being formatted separately here, but ideally all
    // query params are formatted consistently using the comma-format. This
    // requires coordination with updating the backend and various other endpoints
    // so leaving this task alone for now and simply conforming to the existing
    // interface until we are able to upgrade how query params are handled in general.
    // @ts-ignore @TODO Epic WT-21600
    const attributeIdsQueryParam = urls.queryStringify({
      attributeIds: attributeIds || undefined,
    });
    // @ts-ignore @TODO Epic WT-21600
    const queryParams = urls.queryStringify(
      {
        lang,
        companyId,
        surveyIds,
        ...attributeValues,
        isHierarchyReport,
        managerReportIds: managerReportIds || undefined,
      },
      { arrayFormat: 'indices', encode: true },
    ); // REFACTOR: currently overriding default comma-format to conform to existing API

    const url = `${host}/survey-reporting/overview?${queryParams}&${attributeIdsQueryParam}`;
    const res = await httpRequest<any>(url);

    if (res.error) return rejectWithValue(res.error);

    const factors = transformers.itemListFrom(res?.payload?.factors, true);
    const questions = transformers.itemListFrom(res?.payload?.questions, false);
    const highlights = transformers.highlightsFrom(res?.payload?.highlights);
    const permissionLabel = transformers.permissionLabelFrom(res?.payload?.permissionDetails);
    const overview = transformers.overviewFrom(res?.payload);
    const normalizedData = normalize(overview, schemas.overview);

    return {
      factors,
      questions,
      highlights,
      permissionLabel,
      entities: normalizedData.entities,
    };
  },
);

const fetchReportingResults = createAsyncThunk<
  | {
      overallResult: any;
      categoryResults: any[];
      learningContents: any[];
      taskTemplates: any[];
    }
  | unknown,
  {
    surveyIds: number[];
    selectedAttributes: { id: number; values: string[] }[];
    basedOnHierarchy?: boolean;
    selectedTimeframe: TimeframeInput | null;
  }
>(
  'survey/fetchReportingResults',
  async ({ surveyIds, selectedAttributes, basedOnHierarchy = false, selectedTimeframe }, { rejectWithValue }) => {
    const results = await fetchBaseReportQuery({
      surveyIds,
      selectedAttributes: transformers.selectedAttributesFrom(selectedAttributes),
      basedOnHierarchy,
      selectedTimeframe,
    });

    const normalizedResult = normalize(results, {
      overallResult: schemas.overallResult,
      categoryResults: [schemas.categoryResult],
      learningContents: [schemas.learningContent],
      taskTemplates: [schemas.taskTemplate],
    });

    return normalizedResult;
  },
);

const fetchReportingCompareResults = createAsyncThunk<
  { [key: string]: any },
  {
    surveyIds: number[];
    selectedAttributes: { id: number; values: string[] }[];
    isComparedOrgWide?: boolean;
    basedOnHierarchy?: boolean;
    comparedSurveyId: number;
    comparedAttributes: { id: number; values: string[] }[];
    selectedTimeframe?: TimeframeInput;
    selectedCompareToTimeframe?: TimeframeInput;
  }
>(
  'survey/fetchReportingCompareResults',
  async (
    {
      surveyIds,
      selectedAttributes,
      isComparedOrgWide = false,
      basedOnHierarchy = false,
      comparedSurveyId,
      comparedAttributes,
      selectedTimeframe,
      selectedCompareToTimeframe,
    },
    { rejectWithValue },
  ) => {
    const url = `${host}/api/reporting/v1/base-compare-report`;
    const res = await httpRequest<any>(url, {
      method: 'POST',
      body: {
        comparedSurveyId,
        surveyIds,
        basedOnHierarchy,
        isComparedOrgWide,
        selectedAttributes: transformers.selectedAttributesFrom(selectedAttributes),
        comparedAttributes: transformers.selectedAttributesFrom(comparedAttributes),
        selectedTimeframe,
        selectedCompareToTimeframe,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    const results = transformers.reportingCompareResultsFrom(res.compareReport);
    const normalizedResult = normalize(results, [schemas.compareResult]);

    return normalizedResult;
  },
);

const fetchReportingAttributeCompareResults = createAsyncThunk<
  { [key: string]: any },
  {
    surveyIds: number[];
    selectedAttributes: { id: number; values: string[] }[];
    isComparedOrgWide?: boolean;
    basedOnHierarchy?: boolean;
    comparedSurveyId: number;
    comparedAttributes: { id: number; values: string[] }[];
    inputAttributeId?: number;
    requestManagerHierarchies?: boolean;
    selectedTimeframe?: TimeframeInput;
    selectedCompareToTimeframe?: TimeframeInput;
  }
>(
  'survey/fetchReportingAttributeCompareResults',
  async (
    {
      surveyIds,
      selectedAttributes,
      isComparedOrgWide = false,
      basedOnHierarchy = false,
      comparedSurveyId,
      comparedAttributes,
      inputAttributeId = null,
      requestManagerHierarchies = false,
      selectedTimeframe,
      selectedCompareToTimeframe,
    },
    { rejectWithValue },
  ) => {
    const url = `${host}/api/reporting/v1/attribute-compare-report`;
    const res = await httpRequest<any>(url, {
      method: 'POST',
      body: {
        inputAttributeId,
        comparedSurveyId,
        surveyIds,
        basedOnHierarchy,
        isComparedOrgWide,
        requestManagerHierarchies,
        selectedAttributes: transformers.selectedAttributesFrom(selectedAttributes),
        comparedAttributes: transformers.selectedAttributesFrom(comparedAttributes),
        selectedTimeframe,
        selectedCompareToTimeframe,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    const results = transformers.reportingCompareResultsFrom(res.compareReport);
    const normalizedResult = normalize(results, [schemas.compareResult]);

    return normalizedResult;
  },
);

const fetchReportingAttributeResult = createAsyncThunk<
  { currentOverallResultId: number; [key: string]: any },
  {
    surveyIds: number[];
    selectedAttributes: { id: number; values: string[] }[];
    inputAttributeId: number;
    basedOnHierarchy?: boolean;
    requestManagerHierarchies?: boolean;
    selectedTimeframe: TimeframeInput | null;
  },
  { state: RootState }
>(
  'survey/fetchReportingAttributeResult',
  // @ts-ignore @TODO Epic WT-21600
  async (
    {
      surveyIds,
      selectedAttributes,
      inputAttributeId,
      basedOnHierarchy = false,
      requestManagerHierarchies = false,
      selectedTimeframe = null,
    },
    { getState },
  ) => {
    const results = await fetchAttributeReportQuery({
      surveyIds,
      selectedAttributes,
      inputAttributeId,
      basedOnHierarchy,
      requestManagerHierarchies,
      selectedTimeframe,
    });

    const normalizedResult = normalize(results, {
      overallAttributeResult: schemas.attributeResult,
      attributeCategoryResults: [schemas.categoryResult],
    });

    const reportingState = getState().reporting;
    const currentOverallResultId = reportingState.results.overallResult;

    return {
      ...normalizedResult,
      currentOverallResultId,
    };
  },
);

const updateBenchmarkCompareResults = createAction(
  'survey/updateBenchmarkCompareResults',
  (questionResults: QuestionResults, benchmarkResults: BenchmarkResults) => {
    const benchmarkCompareResults = {};
    const STANDARD_SCALE = 5;

    for (const surveyElementId of Object.keys(questionResults)) {
      const questionResult = questionResults[surveyElementId];
      const benchmarkResult = benchmarkResults[surveyElementId];
      const isEnpsQuestion = questionResult.enpsScore !== null;
      if (!benchmarkResult) {
        benchmarkCompareResults[surveyElementId] = {
          scaleFavorableDiffer: null,
          scoreAverageDiffer: null,
          companiesCount: null,
          enpsScoreDiffer: null,
          enpsFavorableDiffer: null,
        };
      } else {
        if (isEnpsQuestion) {
          benchmarkCompareResults[surveyElementId] = {
            scaleFavorableDiffer: null,
            scoreAverageDiffer: null,
            companiesCount: benchmarkResult.companiesCount,
            enpsScoreDiffer: questionResult.enpsScore - benchmarkResult.eNPSScore,
            enpsFavorableDiffer: questionResult.enpsDistribution.favorablePercent - benchmarkResult.promoter,
          };
        } else {
          benchmarkCompareResults[surveyElementId] = {
            scaleFavorableDiffer: questionResult.scaleDistribution.favorablePercent - benchmarkResult.positivePercent,
            scoreAverageDiffer: questionResult.averageScore - benchmarkResult.averageScore / STANDARD_SCALE,
            companiesCount: benchmarkResult.companiesCount,
            enpsScoreDiffer: null,
            enpsFavorableDiffer: null,
          };
        }
      }
    }

    return {
      payload: {
        benchmarkCompareResults,
      },
    };
  },
);

export const surveyReportingActions = {
  fetchReportingAttributeCompareResults,
  fetchReportingAttributeResult,
  fetchReportingCompareResults,
  fetchReportingOverview,
  fetchReportingResults,
  resetReportingState,
  updateBenchmarkCompareResults,
};
